import { useTranslation } from "@sagot/i18n";
import { Button, Field, Icon, Input } from "@sagot/ui";
import { useState, type KeyboardEvent } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { LIMITS, addBlockedDate, type AvailabilityValues } from "./availability-model";

/** `2026-12-25` → "Dec 25, 2026" (a calendar day has no time zone, so it is read as UTC). */
function useDayLabel() {
  const { i18n } = useTranslation();
  const format = new Intl.DateTimeFormat(i18n.language === "fil" ? "fil-PH" : "en-PH", { dateStyle: "medium", timeZone: "UTC" });
  return (isoDate: string) => format.format(new Date(`${isoDate}T00:00:00Z`));
}

/** Closed days: pick a date, "Block date", remove with the × on its chip. */
export function BlockedDatesEditor({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  const dayLabel = useDayLabel();
  const { control, formState } = useFormContext<AvailabilityValues>();
  const { fields, replace } = useFieldArray({ control, name: "blocked_dates" });
  const [draft, setDraft] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const listError = formState.errors.blocked_dates?.message ?? formState.errors.blocked_dates?.root?.message;
  const add = () => {
    const result = addBlockedDate(fields.map((field) => field.date), draft);
    if (!result.ok) {
      setProblem(t(`build.actions.availability.blocked.${result.reason}`, { count: LIMITS.blockedDates }));
      return;
    }
    replace(result.dates.map((date) => ({ date })));
    setDraft("");
    setProblem(null);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    add();
  };
  return (
    <section aria-labelledby="availability-blocked-title">
      <div className="flex items-baseline justify-between gap-3">
        <h3 id="availability-blocked-title" className="text-sm font-bold">{t("build.actions.availability.blocked.title")}</h3>
        <span className="text-xs tabular-nums text-fg-subtle">{fields.length} / {LIMITS.blockedDates}</span>
      </div>
      <p className="mt-0.5 text-xs text-fg-subtle">{t("build.actions.availability.blocked.hint", { count: LIMITS.blockedDates })}</p>
      {disabled ? null : (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <Field label={t("build.actions.availability.blocked.dateLabel")} error={problem}>
            <Input type="date" className="h-9 w-44" value={draft} onChange={(event) => { setDraft(event.target.value); setProblem(null); }} onKeyDown={onKeyDown} />
          </Field>
          <Button type="button" variant="secondary" onClick={add}>
            <Icon name="plus" />
            {t("build.actions.availability.blocked.add")}
          </Button>
        </div>
      )}
      {listError ? <p className="mt-2 text-xs font-semibold text-danger" role="alert">{listError}</p> : null}
      {fields.length === 0 ? (
        <p className="mt-3 text-sm text-fg-subtle">{t("build.actions.availability.blocked.none")}</p>
      ) : (
        <ul className="mt-3 flex flex-wrap gap-2" aria-label={t("build.actions.availability.blocked.list")}>
          {fields.map((field) => (
            <li key={field.id} className="inline-flex items-center gap-1 rounded-pill bg-sunken py-0.5 pl-3 pr-1 text-sm">
              <time dateTime={field.date}>{dayLabel(field.date)}</time>
              {disabled ? null : (
                <Button type="button" variant="ghost" size="sm" iconOnly aria-label={t("build.actions.availability.blocked.remove", { date: field.date })} onClick={() => { replace(fields.filter((f) => f.id !== field.id).map(({ date }) => ({ date }))); }}>
                  <Icon name="x" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
